import {useState, useEffect, useRef} from 'react';

import type {Exponea} from 'globals';
import type {Props, ExponeaArtworkRecommendation} from './typings';

import useWaitForSdkReady from '@/hooks/use-wait-for-sdk-ready/use-wait-for-sdk-ready';
import useExponea from '@/hooks/use-exponea/use-exponea';

const EXPONEA_SDK_TIMEOUT_MS = 1000;

interface FetchedDataState {
  data: ExponeaArtworkRecommendation[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error: null | string;
}

/**
 * @descriptionCustom hook to fetch recommendations from Exponea.
 * It tracks user interactions with the recommendations.
 * @param {object} params - parameters for fetching recommendations.
 * @param {string|undefined} params.recommendationId - (optional) the recommendation ID (see bloomreach dashboard).
 * @param {number|undefined} params.size - (optional, default=4) number of items to fetch.
 * @param {number|undefined} params.productId - (optional) the current product ID to include in the recommendation request.
 * @return {object} An object containing the recommendations, status, and a function to track item clicks.
 */
const useExponeaRecommendations = ({recommendationId, size = 4, productId}: Props) => {
  const {exponea} = useExponea();
  const {isReady: isSdkReady, error: sdkError} = useWaitForSdkReady({
    checkIsReady: () =>
      typeof exponea?.getRecommendation === 'function' && typeof exponea?.track === 'function',
  });
  const [exponeaData, setExponeaData] = useState<FetchedDataState>({
    data: [],
    status: 'idle',
    error: null,
  });

  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleTrackItemClick = (itemId: string | number) => {
    exponea?.track('recommendation', {
      action: 'click',
      item_id: itemId,
      recommendation_id: recommendationId,
    });
  };

  const handleTrackItemsDisplay = (itemIds: (string | number)[]) => {
    exponea?.track('recommendation', {
      action: 'show',
      item_ids: itemIds,
      recommendation_id: recommendationId,
    });
  };

  const fetchRecommendations = () => {
    if (!recommendationId) {
      return;
    }

    setExponeaData((current) => ({...current, status: 'loading'}));

    const options: Parameters<Exponea['getRecommendation']>[0] = {
      recommendationId,
      size,
      fillWithRandom: true,
      catalogFilter: [],
      catalogAttributesWhitelist: ['slug'],
      callback: (receivedData: ExponeaArtworkRecommendation[] | null) => {
        setExponeaData((current) => {
          if (current.error === 'timeout') return current;

          if (!receivedData?.length) {
            return {error: 'No data available', data: [], status: 'error'};
          } else {
            return {error: null, data: receivedData, status: 'success'};
          }
        });
      },
    };

    if (typeof productId !== 'undefined') {
      options.items = {
        /**
         * @description needs to be for example: "1069743": 1
         * @link https://documentation.bloomreach.com/engagement/docs/personalization#getrecommendation
         */
        [productId.toString()]: 1,
      };
    }

    exponea?.getRecommendation(options);

    timeoutRef.current = setTimeout(() => {
      setExponeaData((current) => {
        if (current.data.length === 0) {
          exponea?.track?.('recommendation', {
            action: 'timeout',
            recommendation_id: recommendationId,
          });
          return {error: 'timeout', data: [], status: 'error'};
        }
        return current;
      });
    }, EXPONEA_SDK_TIMEOUT_MS);
  };

  /**
   * @description Fetches recommendations from Exponea. Because the BloomReach script injects
   * the `window.exponea` object asynchronously, we need to wait for its availability
   */
  useEffect(() => {
    if (!isSdkReady || !recommendationId) return;

    fetchRecommendations();

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isSdkReady, recommendationId]);

  useEffect(() => {
    if (sdkError) {
      setExponeaData({error: sdkError, data: [], status: 'error'});
    }
  }, [sdkError]);

  return {
    ...exponeaData,
    handleTrackItemClick,
    handleTrackItemsDisplay,
  };
};

export default useExponeaRecommendations;
